import React, { memo, useMemo } from 'react';
import { Row, Col, Card, Statistic, Typography } from 'antd';
import { TeamOutlined, GlobalOutlined, BankOutlined, StarOutlined, ToolOutlined } from '@ant-design/icons';
import { Ambassador } from '../../types';
import { useAmbassadorStats } from '../../hooks/useAmbassadors';
import { arteliaColors } from '../../theme';

const { Text } = Typography;

interface KPITilesProps {
  ambassadors: Ambassador[];
  filteredAmbassadors: Ambassador[];
}

const KPITiles: React.FC<KPITilesProps> = ({ ambassadors, filteredAmbassadors }) => {
  const totalStats = useAmbassadorStats(ambassadors);
  const rawFilteredStats = useAmbassadorStats(filteredAmbassadors);
  
  // Optimize: Use total stats if no filtering applied (avoid duplicate computation)
  const filteredStats = useMemo(() => {
    if (filteredAmbassadors.length === ambassadors.length) {
      return totalStats; // Use same stats if no filtering applied
    }
    return rawFilteredStats;
  }, [filteredAmbassadors.length, ambassadors.length, totalStats, rawFilteredStats]);

  const kpiData = [
    {
      title: 'Total Ambassadors',
      value: filteredAmbassadors.length,
      total: ambassadors.length,
      icon: <TeamOutlined style={{ color: arteliaColors.bluePrimary }} />,
      color: arteliaColors.bluePrimary,
      suffix: ambassadors.length > filteredAmbassadors.length ? `of ${ambassadors.length}` : undefined,
    },
    {
      title: 'Countries',
      value: Object.keys(filteredStats.byCountry).length,
      total: Object.keys(totalStats.byCountry).length,
      icon: <GlobalOutlined style={{ color: arteliaColors.yellowDarker }} />,
      color: arteliaColors.yellowDarker,
      suffix: Object.keys(totalStats.byCountry).length > Object.keys(filteredStats.byCountry).length 
        ? `of ${Object.keys(totalStats.byCountry).length}` 
        : undefined,
    },
    {
      title: 'Business Units',
      value: Object.keys(filteredStats.byBusinessUnit).length,
      total: Object.keys(totalStats.byBusinessUnit).length,
      icon: <BankOutlined style={{ color: arteliaColors.purpleDarker }} />,
      color: arteliaColors.purpleDarker,
      suffix: Object.keys(totalStats.byBusinessUnit).length > Object.keys(filteredStats.byBusinessUnit).length 
        ? `of ${Object.keys(totalStats.byBusinessUnit).length}` 
        : undefined,
    },
    {
      title: 'Boosters',
      value: filteredStats.byRole['Booster'] || 0,
      total: totalStats.byRole['Booster'] || 0,
      icon: <StarOutlined style={{ color: arteliaColors.greenDarker }} />,
      color: arteliaColors.greenDarker,
      suffix: (totalStats.byRole['Booster'] || 0) > (filteredStats.byRole['Booster'] || 0) 
        ? `of ${totalStats.byRole['Booster'] || 0}` 
        : undefined,
    },
    {
      title: 'Activity Areas',
      value: Object.keys(filteredStats.byActivity).length,
      total: Object.keys(totalStats.byActivity).length,
      icon: <ToolOutlined style={{ color: arteliaColors.redDarker }} />,
      color: arteliaColors.redDarker,
      suffix: Object.keys(totalStats.byActivity).length > Object.keys(filteredStats.byActivity).length 
        ? `of ${Object.keys(totalStats.byActivity).length}` 
        : undefined,
    },
  ];

  return (
    <Row gutter={[16, 16]}>
      {kpiData.map((kpi, index) => (
        <Col xs={24} sm={12} lg={8} xl={4} key={index}>
          <Card
            size="small"
            style={{
              borderLeft: `4px solid ${kpi.color}`,
              height: '100%',
            }}
            styles={{ body: { padding: '16px' } }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ flex: 1 }}>
                <Statistic
                  title={
                    <Text style={{ fontSize: '12px', color: '#666', fontWeight: 500 }}>
                      {kpi.title}
                    </Text>
                  }
                  value={kpi.value}
                  suffix={kpi.suffix && (
                    <Text style={{ fontSize: '14px', color: '#999' }}>
                      {kpi.suffix}
                    </Text>
                  )}
                  valueStyle={{
                    fontSize: '24px',
                    fontWeight: 600,
                    color: kpi.color,
                    lineHeight: 1.2,
                  }}
                />
                {kpi.total !== kpi.value && (
                  <div style={{ marginTop: '4px' }}>
                    <Text style={{ fontSize: '11px', color: '#999' }}>
                      {((kpi.value / kpi.total) * 100).toFixed(0)}% of total
                    </Text>
                  </div>
                )}
              </div>
              <div style={{ 
                fontSize: '24px', 
                opacity: 0.7,
                marginLeft: '12px'
              }}>
                {kpi.icon}
              </div>
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

// Memoize KPITiles to prevent unnecessary re-renders
export default memo(KPITiles);